/**
 * Team Management Page
 * 
 * Dashboard page for managing team members, invitations, and roles.
 * 
 * HIPAA NOTE: User identity (email, name) is not PHI.
 * Team management actions are logged to audit trail.
 */

import { useState, useEffect, useCallback } from 'react';
import { DashboardLayout, EmptyState } from '../../components/dashboard';
import { useAuth } from '../../contexts/AuthContext';
import type { TeamMember, UserRole, InviteUserRequest } from '@formflow/shared';
import { teamApi, ApiClientError, type RoleInfo, type TeamStats } from '../../lib/api';
import './TeamPage.css';

// Role badge colors
const ROLE_COLORS: Record<UserRole, string> = {
  owner: '#6366f1',   // Indigo
  admin: '#8b5cf6',   // Purple
  editor: '#0891b2',  // Cyan
  viewer: '#64748b',  // Slate
};

export function TeamPage() {
  const { user } = useAuth();
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [roles, setRoles] = useState<RoleInfo[]>([]);
  const [stats, setStats] = useState<TeamStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Modal states
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  
  // Invite form state
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<UserRole>('viewer');
  const [inviteFirstName, setInviteFirstName] = useState('');
  const [inviteLastName, setInviteLastName] = useState('');
  const [isInviting, setIsInviting] = useState(false);

  // Check if current user can manage team (admin or owner)
  const canManageTeam = user?.role === 'owner' || user?.role === 'admin';

  // Fetch team data
  const fetchTeamData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const [membersResponse, rolesResponse, statsResponse] = await Promise.all([
        teamApi.list(),
        teamApi.getRoles(),
        teamApi.getStats(),
      ]);
      
      setMembers(membersResponse.data || []);
      setRoles(rolesResponse.data || []);
      setStats(statsResponse.data || null);
    } catch (e) {
      const message = e instanceof ApiClientError
        ? e.error.message
        : 'Failed to load team data';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTeamData();
  }, [fetchTeamData]);

  // Clear messages after timeout
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Invite new member
  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    
    setIsInviting(true);
    setError(null);
    
    try {
      const data: InviteUserRequest = {
        email: inviteEmail.trim(),
        role: inviteRole,
      };
      if (inviteFirstName.trim()) data.first_name = inviteFirstName.trim();
      if (inviteLastName.trim()) data.last_name = inviteLastName.trim();
      
      const response = await teamApi.invite(data);
      setMembers([response.data, ...members]);
      setSuccessMessage(`Invitation sent to ${inviteEmail}`);
      closeInviteModal();
      
      // Refresh stats
      const statsResponse = await teamApi.getStats();
      setStats(statsResponse.data || null);
    } catch (e) {
      const message = e instanceof ApiClientError
        ? e.error.message
        : 'Failed to send invitation';
      setError(message);
    } finally {
      setIsInviting(false);
    }
  };

  // Update member role
  const handleUpdateRole = async (newRole: UserRole) => {
    if (!selectedMember) return;
    
    try {
      const response = await teamApi.updateRole(selectedMember.id, { role: newRole });
      setMembers(members.map(m => 
        m.id === selectedMember.id ? response.data : m
      ));
      setSuccessMessage(`Updated ${selectedMember.email} to ${newRole}`);
      closeRoleModal();
      
      // Refresh stats
      const statsResponse = await teamApi.getStats();
      setStats(statsResponse.data || null);
    } catch (e) {
      const message = e instanceof ApiClientError
        ? e.error.message
        : 'Failed to update role';
      setError(message);
    }
  };

  // Remove member
  const handleRemoveMember = async (member: TeamMember) => {
    if (!confirm(`Are you sure you want to remove ${member.email} from the team?`)) return;
    
    try {
      await teamApi.remove(member.id);
      setMembers(members.filter(m => m.id !== member.id));
      setSuccessMessage(`${member.email} has been removed from the team`);
      
      // Refresh stats
      const statsResponse = await teamApi.getStats();
      setStats(statsResponse.data || null);
    } catch (e) {
      const message = e instanceof ApiClientError
        ? e.error.message
        : 'Failed to remove member';
      setError(message);
    }
  };

  // Resend invitation
  const handleResendInvitation = async (member: TeamMember) => {
    try {
      await teamApi.resendInvitation(member.id);
      setSuccessMessage(`Invitation resent to ${member.email}`);
    } catch (e) {
      const message = e instanceof ApiClientError
        ? e.error.message
        : 'Failed to resend invitation';
      setError(message);
    }
  };

  // Modal helpers
  const closeInviteModal = () => {
    setShowInviteModal(false);
    setInviteEmail('');
    setInviteRole('viewer');
    setInviteFirstName('');
    setInviteLastName('');
  };

  const openRoleModal = (member: TeamMember) => {
    setSelectedMember(member);
    setShowRoleModal(true);
  };

  const closeRoleModal = () => {
    setShowRoleModal(false);
    setSelectedMember(null);
  };

  // Format date
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Get initials
  const getInitials = (firstName: string, lastName: string, email: string) => {
    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase();
    }
    return email.slice(0, 2).toUpperCase();
  };

  // Get full name or email
  const getDisplayName = (member: TeamMember) => {
    if (member.first_name && member.last_name) {
      return `${member.first_name} ${member.last_name}`;
    }
    return member.email;
  };

  // Check if current user can manage this member
  const canManageMember = (member: TeamMember) => {
    if (!canManageTeam) return false;
    if (member.email === user?.email) return false; // Can't manage yourself
    
    // Role hierarchy check
    const roleHierarchy: Record<UserRole, number> = {
      owner: 100,
      admin: 75,
      editor: 50,
      viewer: 25,
    };
    
    const userLevel = roleHierarchy[user?.role || 'viewer'];
    const memberLevel = roleHierarchy[member.role];
    
    return userLevel > memberLevel;
  };

  // Role Badge Component
  const RoleBadge = ({ role }: { role: UserRole }) => (
    <span 
      className="role-badge"
      style={{ backgroundColor: ROLE_COLORS[role] }}
    >
      {role.charAt(0).toUpperCase() + role.slice(1)}
    </span>
  );

  // MFA Badge Component
  const MfaBadge = ({ enabled }: { enabled: boolean }) => (
    <span className={`mfa-badge ${enabled ? 'mfa-enabled' : 'mfa-disabled'}`}>
      {enabled ? '🔒 MFA' : '⚠️ No MFA'}
    </span>
  );

  return (
    <DashboardLayout title="Team" subtitle="Manage your team members and roles">
      {/* Stats Cards */}
      {stats && (
        <div className="team-stats">
          <div className="stat-card">
            <span className="stat-value">{stats.total_members}</span>
            <span className="stat-label">Total Members</span>
          </div>
          <div className="stat-card">
            <span className="stat-value">{stats.by_role.owner + stats.by_role.admin}</span>
            <span className="stat-label">Admins</span>
          </div>
          <div className="stat-card">
            <span className="stat-value">{stats.by_role.editor}</span>
            <span className="stat-label">Editors</span>
          </div>
          <div className="stat-card">
            <span className="stat-value">{stats.by_role.viewer}</span>
            <span className="stat-label">Viewers</span>
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="team-toolbar">
        <div className="toolbar-left">
          <h3>Team Members</h3>
        </div>
        
        {canManageTeam && (
          <button
            className="btn btn-primary"
            onClick={() => setShowInviteModal(true)}
          >
            + Invite Member
          </button>
        )}
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="success-banner">
          <span>✓ {successMessage}</span>
          <button onClick={() => setSuccessMessage(null)}>×</button>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="error-banner">
          <span>⚠️ {error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading team...</p>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && members.length === 0 && (
        <EmptyState
          title="No team members"
          description="Invite team members to collaborate on forms"
          actionLabel={canManageTeam ? "Invite Member" : undefined}
          onAction={canManageTeam ? () => setShowInviteModal(true) : undefined}
        />
      )}

      {/* Members List */}
      {!isLoading && members.length > 0 && (
        <div className="members-list">
          {members.map((member) => (
            <div key={member.id} className="member-card">
              <div className="member-avatar">
                {getInitials(member.first_name, member.last_name, member.email)}
              </div>
              
              <div className="member-info">
                <div className="member-name">
                  {getDisplayName(member)}
                  {member.email === user?.email && (
                    <span className="you-badge">You</span>
                  )}
                </div>
                <div className="member-email">{member.email}</div>
                <div className="member-meta">
                  <span>Joined {formatDate(member.created_at)}</span>
                  <span>·</span>
                  <span>Last login {formatDate(member.last_login)}</span>
                </div>
              </div>
              
              <div className="member-badges">
                <RoleBadge role={member.role} />
                <MfaBadge enabled={member.mfa_enabled} />
              </div>
              
              {canManageMember(member) && (
                <div className="member-actions">
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => openRoleModal(member)}
                  >
                    Change Role
                  </button>
                  <div className="dropdown">
                    <button className="btn btn-sm btn-icon">⋮</button>
                    <div className="dropdown-menu">
                      <button onClick={() => handleResendInvitation(member)}>
                        Resend Invitation
                      </button>
                      <button
                        className="danger"
                        onClick={() => handleRemoveMember(member)}
                      >
                        Remove from Team
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Roles Info */}
      {roles.length > 0 && (
        <div className="roles-info">
          <h3>Available Roles</h3>
          <div className="roles-grid">
            {roles.map((role) => (
              <div key={role.name} className="role-card">
                <div 
                  className="role-header"
                  style={{ borderLeftColor: ROLE_COLORS[role.name as UserRole] }}
                >
                  <span className="role-name">{role.display_name}</span>
                </div>
                <p className="role-description">{role.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="modal-overlay" onClick={closeInviteModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Invite Team Member</h2>
            <p className="modal-description">
              Send an invitation to join your team. They'll receive an email with instructions to set up their account.
            </p>
            
            <div className="form-group">
              <label htmlFor="inviteEmail">Email Address *</label>
              <input
                type="email"
                id="inviteEmail"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="colleague@company.com"
                autoFocus
              />
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="inviteFirstName">First Name</label>
                <input
                  type="text"
                  id="inviteFirstName"
                  value={inviteFirstName}
                  onChange={(e) => setInviteFirstName(e.target.value)}
                  placeholder="John"
                />
              </div>
              <div className="form-group">
                <label htmlFor="inviteLastName">Last Name</label>
                <input
                  type="text"
                  id="inviteLastName"
                  value={inviteLastName}
                  onChange={(e) => setInviteLastName(e.target.value)}
                  placeholder="Doe"
                />
              </div>
            </div>
            
            <div className="form-group">
              <label htmlFor="inviteRole">Role *</label>
              <select
                id="inviteRole"
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value as UserRole)}
              >
                {roles
                  .filter(r => {
                    // Can't invite someone with equal or higher role
                    const roleHierarchy: Record<string, number> = {
                      owner: 100,
                      admin: 75,
                      editor: 50,
                      viewer: 25,
                    };
                    const userLevel = roleHierarchy[user?.role || 'viewer'];
                    return roleHierarchy[r.name] < userLevel;
                  })
                  .map((role) => (
                    <option key={role.name} value={role.name}>
                      {role.display_name}
                    </option>
                  ))
                }
              </select>
              {roles.find(r => r.name === inviteRole) && (
                <p className="form-help">
                  {roles.find(r => r.name === inviteRole)?.description}
                </p>
              )}
            </div>
            
            <div className="modal-actions">
              <button
                className="btn btn-secondary"
                onClick={closeInviteModal}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleInvite}
                disabled={!inviteEmail.trim() || isInviting}
              >
                {isInviting ? 'Sending...' : 'Send Invitation'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Role Modal */}
      {showRoleModal && selectedMember && (
        <div className="modal-overlay" onClick={closeRoleModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Change Role</h2>
            <p className="modal-description">
              Change the role for <strong>{getDisplayName(selectedMember)}</strong>
            </p>
            
            <div className="role-selector">
              {roles
                .filter(r => {
                  // Can't assign equal or higher role
                  const roleHierarchy: Record<string, number> = {
                    owner: 100,
                    admin: 75,
                    editor: 50,
                    viewer: 25,
                  };
                  const userLevel = roleHierarchy[user?.role || 'viewer'];
                  return roleHierarchy[r.name] < userLevel;
                })
                .map((role) => (
                  <div
                    key={role.name}
                    className={`role-option ${selectedMember.role === role.name ? 'active' : ''}`}
                    onClick={() => handleUpdateRole(role.name as UserRole)}
                  >
                    <div 
                      className="role-option-badge"
                      style={{ backgroundColor: ROLE_COLORS[role.name as UserRole] }}
                    >
                      {role.display_name}
                    </div>
                    <p className="role-option-description">{role.description}</p>
                  </div>
                ))
              }
            </div>
            
            <div className="modal-actions">
              <button
                className="btn btn-secondary"
                onClick={closeRoleModal}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

export default TeamPage;
